import { formatNumber, formatPercent } from '@angular/common';
import { Injectable } from '@angular/core';
import { ColDef, SideBarDef, ValueFormatterParams } from 'ag-grid-community';
import { Header } from '@approot/models/ag-grid.model';
import { DateUtilities } from '@approot/utilities';

@Injectable({
  providedIn: 'root'
})
export class AgGridService {
  public headers: Header[]; // all headers

  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    sortable: true
  };

  public sideBarDef: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel'
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel'
      }
    ]
  };
  constructor() {}

  public formatHeaders(headers: Header[]): Header[] {
    // format headers for ag grid
    return headers.map(header => {
      // loop through the headers

      // convert fields to booleans
      this.convertFieldsToBoolean(header);

      if (header.valueFormatter) {
        header.valueFormatter = this.setValueFormatter(header);
      }
      if (typeof header.filter === 'string') {
        // if the filter is a string we need to assign a function to the field
        this.handleHeaderFilter(header);
      }
      if (header.comparator) {
        // used for sorting
        header.comparator = this.handleHeaderComparator(header.comparator);
      }
      if (header.children) {
        // children headers are formatted
        header.children = this.formatHeaders(header.children);
      }
      return header;
    });
  }

  public convertFieldsToBoolean(header: Header) {
    /* backend cant return booleans in certain cases,
    but instead returns 1 or 0 so convert these into booleans */
    header.enablePivot = !!header.enablePivot;
    header.enableRowGroup = !!header.enableRowGroup;
    header.resizable = !!header.resizable;
    header.sortable = !!header.sortable;
    header.headerCheckboxSelection = !!header.headerCheckboxSelection;
    header.checkboxSelection = !!header.checkboxSelection;
    header.headerCheckboxSelectionFilteredOnly =
      !!header.headerCheckboxSelectionFilteredOnly;
    if (typeof header.filter === 'number') {
      header.filter = !!header.filter;
    }
  }

  public handleHeaderFilter(header: Header) {
    if (header.filter === 'agDateColumnFilter') {
      header.filterParams = {
        // provide comparator function
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
          return DateUtilities.filterDateComparator(
            filterLocalDateAtMidnight,
            cellValue
          );
        }
      };
    }
  }

  public handleHeaderComparator(header) {
    switch (header.comparator) {
      case 'dateComparator':
        return DateUtilities.dateComparator;
      default:
        return null;
    }
  }

  public setValueFormatter(header: Header) {
    // agGrids valueFormatter requires a function
    switch (header.valueFormatter) {
      case 'float':
        return this.floatFormatter.bind(this, header);
      case 'date':
        return this.dateFormatter;
      case 'percent':
        return this.percentFormatter.bind(this, header.nDecimals);
      default:
        return null;
    }
  }

  public percentFormatter(
    nDecimals: number,
    row: ValueFormatterParams
  ): string {
    if (row.value != null) {
      const decimalPlaces = nDecimals || 0;
      return formatPercent(
        row.value,
        'en-GB',
        `1.${decimalPlaces}-${decimalPlaces}`
      );
    }
    return null;
  }

  public floatFormatter(header: Header, row: ValueFormatterParams): string {
    let displayValue = row.value;

    if (displayValue == null) {
      if (header.displayNullAsBlank) {
        return '';
      }
      displayValue = 0;
    }

    const decimalPlaces = header.nDecimals ?? 0;

    return formatNumber(
      displayValue,
      'en-GB',
      '1.' + decimalPlaces + '-' + decimalPlaces // format to nDecimals (the number of decimal places)
    );
  }

  public dateFormatter(row: ValueFormatterParams): string {
    return row.value ? DateUtilities.dateToPrettyDate(row.value) : '';
  }
}
